import { useEffect } from "react";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) {
    el.setAttribute("content", content);
  } else {
    const meta = document.createElement("meta");
    meta.setAttribute(attr, key);
    meta.setAttribute("content", content);
    document.head.appendChild(meta);
  }
}

function setJsonLd(id: string, data: object) {
  const existing = document.getElementById(`jsonld-${id}`);
  const text = JSON.stringify(data);
  if (existing) {
    existing.textContent = text;
    return;
  }
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = `jsonld-${id}`;
  script.textContent = text;
  document.head.appendChild(script);
}

export interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  jsonLd?: object;
  jsonLdId?: string;
}

/**
 * 页面级 SEO：更新 title / description / keywords / OG / canonical / JSON-LD。
 */
export function useSeo(options: SeoOptions) {
  const { title, description, keywords, canonicalPath, ogType, jsonLd, jsonLdId } = options;
  useEffect(() => {
    const base = (window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ || "";
    const canonicalUrl = `${window.location.origin}${base}${canonicalPath || "/"}`;
    document.title = title;
    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", ogType || "website");
    upsertMeta("property", "og:url", canonicalUrl);
    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", canonicalUrl);
    } else {
      const link = document.createElement("link");
      link.rel = "canonical";
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }
    if (jsonLd && jsonLdId) setJsonLd(jsonLdId, jsonLd);
    return () => {
      const node = document.getElementById(`jsonld-${jsonLdId}`);
      if (node) node.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}